// src/components/resources/tools/generators/FakeChat/avatarNameColors.ts

/**
 * Sender-name / username colour maps keyed by the avatar swatch values offered
 * in `FakeChatControls` (`avatarSwatches`). WhatsApp and Discord both colour an
 * incoming participant's name by their avatar swatch, but with different
 * palettes — WhatsApp uses muted Tailwind text tones, Discord uses its role
 * colours. Previously each preview carried its own `switch` over the same six
 * swatches in two files; centralising both palettes here keeps them in lockstep
 * with the swatch list (one place to edit when a swatch is added) and is
 * guarded by `avatarNameColors.test.ts`, which asserts every swatch resolves in
 * both maps.
 */

/** Avatar swatch values exposed by `FakeChatControls.avatarSwatches`. */
export const AVATAR_SWATCHES = [
  'bg-indigo-500',
  'bg-emerald-500',
  'bg-sky-500',
  'bg-rose-500',
  'bg-slate-500',
  'bg-amber-500',
] as const

export type AvatarSwatch = (typeof AVATAR_SWATCHES)[number]

/** WhatsApp group sender-name colour per swatch; falls back to WhatsApp teal. */
const WHATSAPP_NAME_COLORS: Record<AvatarSwatch, string> = {
  'bg-indigo-500': 'text-indigo-600',
  'bg-emerald-500': 'text-emerald-600',
  'bg-sky-500': 'text-sky-600',
  'bg-rose-500': 'text-rose-600',
  'bg-slate-500': 'text-slate-600',
  'bg-amber-500': 'text-amber-600',
}
const WHATSAPP_NAME_FALLBACK = 'text-[#06cf9c]'

/** Discord username colour per swatch; falls back to Discord's default text. */
const DISCORD_NAME_COLORS: Record<AvatarSwatch, string> = {
  'bg-indigo-500': 'text-[#949cf7]',
  'bg-emerald-500': 'text-[#3ba55d]',
  'bg-sky-500': 'text-[#3498db]',
  'bg-rose-500': 'text-[#eb459e]',
  'bg-slate-500': 'text-[#b9bbbe]',
  'bg-amber-500': 'text-[#faa61a]',
}
const DISCORD_NAME_FALLBACK = 'text-white'

/** Maps an avatar swatch to its WhatsApp sender-name colour. */
export function whatsappNameColor(avatarColor?: string): string {
  return WHATSAPP_NAME_COLORS[avatarColor as AvatarSwatch] ?? WHATSAPP_NAME_FALLBACK
}

/** Maps an avatar swatch to its Discord username colour. */
export function discordNameColor(avatarColor?: string): string {
  return DISCORD_NAME_COLORS[avatarColor as AvatarSwatch] ?? DISCORD_NAME_FALLBACK
}
